import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // outline rounded doesn't exist?
import { Box, IconButton, Popover, Stack, Typography } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AudioApi } from '../../api/AudioApi';
import { DialogPromptIntroduceWord } from '../../api/DialogPromptApi';
import AppBarContext from '../../context/AppBarContext';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import { scrollElementIntoViewWithFallback } from '../../util/scrollUtils';
import ChatBubble from '../ChatBubble';
import { handleErrorMessage } from '../ErrorDialog';
import { TeacherAudioActions, getSelectedTeacherSpeakerRole } from '../TeacherAudioReportButton';
import WordPinyin from '../WordPinyin';
import { getAudioByText } from './DialogPromptComponent';

interface DialogPromptIntroduceWordComponentProps {
    dialogPrompt: DialogPromptIntroduceWord;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

const DialogPromptIntroduceWordComponent: React.FC<DialogPromptIntroduceWordComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { playAudio } = useAudioPlayer();

    const { setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const storedLearnWithCharacter = localStorage.getItem('learnWithCharacter');
    const learnWithCharacter = storedLearnWithCharacter ? JSON.parse(storedLearnWithCharacter) : false;

    const [chatBubbleDone, setChatBubbleDone] = React.useState(false);

    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);

    React.useEffect(() => {
        clearFooterList()
        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio)
    }, [clearFooterList, dialogPrompt.prompt, history, setErrorMessage]);

    const handleSpeakerClick = React.useCallback(() => {
        const audioPromise = AudioApi.getTeacherSentence(dialogPrompt.sentence_id)

        audioPromise
            .then(response => {
                // check if the response was successful
                if (response.status >= 300) {
                    throw new Error('Getting male audio was not ok statuscode=' + response.status + " " + response.statusText);
                }
                return response.data;
            })
            .then(blob => {
                playAudio(blob, { onEnded: () => { setDialogPromptComplete(dialogPrompt.id) } })
                // create a new Audio object and set the source to the blob URL
                // const audio = new Audio(URL.createObjectURL(blob));
                // audio.addEventListener('ended', () => { setDialogPromptComplete(dialogPrompt.id) }); // Listen to the 'ended' event
                // audio.play();
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [dialogPrompt.sentence_id, dialogPrompt.id, playAudio, setDialogPromptComplete, history, location, setErrorMessage]);

    const [infoAnchorEl, setInfoAnchorEl] = React.useState<null | HTMLElement>(null);
    const infoOpen = Boolean(infoAnchorEl);

    const handleInfoClick = (event: React.MouseEvent<HTMLElement>) => {
        setInfoAnchorEl(event.currentTarget);
    };

    const handleInfoClose = () => {
        setInfoAnchorEl(null);
    };

    const wordAudioReportContext = React.useMemo(() => ({
        sentenceId: dialogPrompt.sentence_id,
        dialogPromptId: dialogPrompt.id,
        textToSpeechSpeakerId: getSelectedTeacherSpeakerRole(),
        audioText: dialogPrompt.new_word,
        audioRole: 'introduce_word_card',
    }), [dialogPrompt.id, dialogPrompt.new_word, dialogPrompt.sentence_id]);

    React.useEffect(() => {
        if (!chatBubbleDone) return;
        scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
        setActionList([
            <TeacherAudioActions context={wordAudioReportContext} onPlay={handleSpeakerClick} showReportMenu={false} />
        ]);
    }, [chatBubbleDone, chatBubbleContainerRef, handleSpeakerClick, setActionList, wordAudioReportContext]);


    return (
        <Stack>
            <ChatBubble
                isTeacher={dialogPrompt.is_teacher}
                message={dialogPrompt.prompt}
                messageAudio={promptAudio ? promptAudio : null}
                showAvatar={showAvatar}
                setChatBubbleDone={setChatBubbleDone}
                chatBubbleContainerRef={chatBubbleContainerRef}
                dialogPromptId={dialogPrompt.id}
                audioRole="prompt"
            />
            {chatBubbleDone && (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', pb: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" className="border-dark"
                        flexWrap="wrap" sx={{ pb: 1, pl: 2, pr: 3, display: 'inline-flex' }}>
                        <TeacherAudioActions context={wordAudioReportContext} onPlay={handleSpeakerClick} />
                        <WordPinyin
                            text={dialogPrompt.new_word}
                            pinyinList={dialogPrompt.new_word_pinyin}
                            showCharacter={learnWithCharacter}
                        />
                        {dialogPrompt.new_word_info_text && (<>
                            <IconButton onClick={handleInfoClick}>
                                <InfoOutlinedIcon sx={{
                                    width: 30,          // bigger width
                                    height: 30,         // bigger height
                                    color: '#0062FF'
                                }} />
                            </IconButton>
                            <Popover
                                open={infoOpen}
                                anchorEl={infoAnchorEl}
                                onClose={handleInfoClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                            >
                                <Box sx={{ p: 2, maxWidth: '300px' }}>
                                    <Typography variant="body2">{dialogPrompt.new_word_info_text}</Typography>
                                </Box>
                            </Popover>
                        </>)}
                    </Stack>
                </Box>
            )}
        </Stack>
    );
};

export default DialogPromptIntroduceWordComponent;
